/**
 * Project Slideshow Initialization
 * 
 * Handles Swiper.js initialization with a custom responsive strategy:
 * 1. Captures original DOM structure to allow clean re-initialization.
 * 2. On mobile (< 992px), multi-column slides are split into individual slides.
 * 3. Monitors window resize/breakpoint changes to re-structure and re-init Swiper.
 * 4. Implements custom mouse-position based navigation (left/right click).
 */

import Swiper from 'swiper';
import { Navigation, Pagination, EffectFade, Autoplay } from 'swiper/modules';

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/css/autoplay";

window.addEventListener('load', function () {
    const containers = document.querySelectorAll('.project-slider');
    containers.forEach(container => {
        const wrapper = container.querySelector('.swiper-wrapper');
        if (!wrapper) return;

        const originalHTML = wrapper.innerHTML;
        const transition = container.dataset.transition || 'slide';
        const autoplay = parseInt(container.dataset.autoplay) === 1;
        const delay = parseInt(container.dataset.delay) || 3000;
        const speed = parseInt(container.dataset.speed) || (transition === 'fade' ? 1000 : 300);
        const showPagination = parseInt(container.dataset.showPagination) === 1;
        const showArrows = parseInt(container.dataset.showArrows) === 1;

        let swiper = null;
        const mediaQuery = window.matchMedia('(max-width: 991px)');

        /**
         * Initializes or re-initializes the Swiper instance.
         * Destroys existing instance and restores original HTML before re-applying 
         * mobile transformation logic if the breakpoint matches.
         */
        const init = () => {
            if (swiper) {
                swiper.destroy(true, true);
                wrapper.innerHTML = originalHTML;
            }

            if (mediaQuery.matches) {
                const slides = wrapper.querySelectorAll('.swiper-slide');
                slides.forEach(slide => {
                    const columns = slide.querySelectorAll('.slide-column');
                    if (columns.length > 1) {
                        columns.forEach(col => {
                            const newSlide = document.createElement('div');
                            newSlide.className = slide.className;
                            newSlide.innerHTML = col.outerHTML;
                            wrapper.insertBefore(newSlide, slide);
                        });
                        slide.remove();
                    }
                });
            }

            swiper = new Swiper(container, {
                modules: [ Navigation, Pagination, EffectFade, Autoplay ],
                effect: transition,
                spaceBetween: 0,
                speed: speed,
                watchSlidesProgress: true,
                loop: true,
                autoplay: autoplay ? {
                    delay: delay,
                    disableOnInteraction: false,
                } : false,
                fadeEffect: {
                    crossFade: transition === 'fade'
                },
                navigation: showArrows ? {
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                } : false,
                pagination: showPagination ? {
                    el: '.swiper-pagination',
                    type: 'bullets',
                } : false,
                on: {
                    init: function () {
                        this.el.querySelectorAll('.swiper-slide-active, .swiper-slide-duplicate-active').forEach(s => s.classList.add('is-animating'));
                    },
                    slideChangeTransitionStart: function () {
                        this.el.querySelectorAll('.swiper-slide-active, .swiper-slide-duplicate-active').forEach(s => s.classList.add('is-animating'));
                    },
                    slideChangeTransitionEnd: function () {
                        this.slides.forEach((slide) => {
                            if (!slide.classList.contains('swiper-slide-active') && !slide.classList.contains('swiper-slide-duplicate-active')) {
                                slide.classList.remove('is-animating');
                            }
                        });
                    }
                }
            });
        };

        init();

        // Listen for breakpoint changes to recompute slides and re-init Swiper
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', init);
        } else {
            // Fallback for older browsers
            mediaQuery.addListener(init);
        }

        // Custom Mouse-based Navigation Logic
        if (showArrows) {
            container.addEventListener('mousemove', function (e) {
                const rect = container.getBoundingClientRect();
                const x = e.clientX - rect.left;
                if (x < rect.width / 2) {
                    container.classList.add('cursor-prev');
                    container.classList.remove('cursor-next');
                } else {
                    container.classList.add('cursor-next');
                    container.classList.remove('cursor-prev');
                }
            });

            container.addEventListener('mouseleave', function () {
                container.classList.remove('cursor-prev', 'cursor-next');
            });

            // Handle click navigation (left half = prev, right half = next)
            container.addEventListener('click', function (e) {
                // Ignore clicks on interactive elements
                if (e.target.closest('.swiper-button-next, .swiper-button-prev, .swiper-pagination, a, button, input, select, textarea')) {
                    return;
                }

                const rect = container.getBoundingClientRect();
                const x = e.clientX - rect.left;
                if (x < rect.width / 2) {
                    if (swiper) swiper.slidePrev();
                } else {
                    if (swiper) swiper.slideNext();
                }
            });
        }
    });
});